// scripts/*.arena files: per map display name, briefing and author, as read by
// the ET map selection (UI_ParseArenas). Same token rules as shader scripts.

import type { VirtualFS } from '../fs/vfs';
import { tokenize } from './tokenize';

export interface ArenaInfo {
  map: string;
  /** May contain ET colour codes (^1 ...). */
  longname?: string;
  briefing?: string;
  author?: string;
  /** Every key of the block, lower-cased keys. */
  keys: Record<string, string>;
}

/** Parses an .arena file: a list of { key "value" ... } blocks. */
export function parseArena(text: string): ArenaInfo[] {
  const tokens = tokenize(text);
  const out: ArenaInfo[] = [];
  let i = 0;
  while (i < tokens.length) {
    if (tokens[i].text !== '{' || tokens[i].quoted) {
      i++;
      continue;
    }
    i++;
    const keys: Record<string, string> = {};
    while (i < tokens.length && !(tokens[i].text === '}' && !tokens[i].quoted)) {
      const key = tokens[i].text.toLowerCase();
      const value = tokens[i + 1];
      // a key without a value on its line (or the end of the block)
      if (!value || value.newline || (value.text === '}' && !value.quoted)) {
        i++;
        continue;
      }
      keys[key] = value.text;
      i += 2;
    }
    i++;
    if (!keys.map) continue;
    out.push({
      map: keys.map.toLowerCase(),
      longname: keys.longname || undefined,
      // ET uses '*' for line breaks in briefings
      briefing: keys.briefing ? keys.briefing.replace(/\*/g, '\n').trim() || undefined : undefined,
      author: keys.author || undefined,
      keys,
    });
  }
  return out;
}

/** Reads every scripts/*.arena file: map name -> info (later pk3s override earlier ones). */
export async function loadArenas(vfs: VirtualFS): Promise<Map<string, ArenaInfo>> {
  const out = new Map<string, ArenaInfo>();
  for (const path of vfs.list('scripts', '.arena')) {
    const text = await vfs.readText(path);
    if (!text) continue;
    for (const info of parseArena(text)) out.set(info.map, info);
  }
  return out;
}
